import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Combobox,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { getProjectLabels } from "@v2/utils/project-labels";
import { getProjectName } from "@v2/utils/project-utils";
import { routes } from "@v2/utils/router";
import { Controller, useForm } from "react-hook-form";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

const validationSchema = z.union([
  z.object({
    projectId: z.string(),
  }),
]);
type ProjectFormValues = z.infer<typeof validationSchema>;

export const PendingShopifyIntegrationModal: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [searchParams] = useSearchParams();
  const pendingAppInstallationId = searchParams.get("pendingAppInstallationId");
  const projectLabels = getProjectLabels();
  const { handleSubmit, formState, control } = useForm<ProjectFormValues>({
    mode: "onChange",
    resolver: zodResolver(validationSchema),
    defaultValues: {
      projectId: undefined,
    },
  });
  const navigate = useNavigate();

  const { mutate: connectIntegration, isPending: isConnectingWorkspace } =
    projectRouter.connectToPendingInstallation.useMutation({
      onSuccess: (_, { projectId }) => {
        void projectUtils.listWithStats.invalidate();
        navigate(
          generatePath(routes.shops.home, {
            projectId,
          }),
        );
        onOpenChange(false);
      },
    });

  const { data: projects, isLoading: isLoadingProjects } =
    projectRouter.listWithStats.useQuery({});
  const projectItems: ComboboxItemWithValue[] =
    projects?.projects
      .filter((project) => !project.integrations?.shopify)
      .map((project) => ({
        label: getProjectName(project),
        description: project.ownerWorkspace.name,
        value: project.id,
        size: "lg",
      })) ?? [];

  const onSubmit = (formValues: ProjectFormValues) => {
    if (formValues.projectId && pendingAppInstallationId) {
      connectIntegration({
        projectId: formValues.projectId,
        shopify: {
          pendingAppInstallationId,
        },
      });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      dismissable={false}
      title="Thanks for connecting a Shopify store"
      size="base"
      description={`Select a ${projectLabels.singularCapitalized} to add this Shopify Integration to. If you don't see the ${projectLabels.singularCapitalized} you want, create a new ${projectLabels.singularCapitalized} first, then connect this Shopify Integration in the ${projectLabels.singularCapitalized}'s settings.`}
      footer={
        <div className="flex flex-row justify-end">
          <Button
            type="button"
            variant="primary"
            size="base"
            loading={isConnectingWorkspace}
            disabled={
              !formState.isValid || isConnectingWorkspace || isLoadingProjects
            }
            onClick={() => void handleSubmit(onSubmit)()}
          >
            {`Connect ${projectLabels.singularCapitalized}`}
          </Button>
        </div>
      }
    >
      <LabeledControl label={projectLabels.singularCapitalized}>
        <Controller
          render={({ field: { onChange, value } }) => {
            const selectedItem =
              projectItems.find((item) => item.value === value) ?? null;
            const displayTitle = selectedItem
              ? `${String(selectedItem.label)} (${selectedItem.description ?? ""})`
              : `Choose ${projectLabels.singularCapitalized}`;
            return (
              <Combobox
                items={projectItems}
                value={selectedItem}
                onValueChange={(item) => {
                  onChange(item?.value ?? null);
                }}
                isItemEqualToValue={(a, b) => a.value === b.value}
                trigger={
                  <ComboboxTriggerButton
                    size="base"
                    isPlaceholder={!selectedItem}
                    className="w-full"
                  >
                    {displayTitle}
                  </ComboboxTriggerButton>
                }
                triggerAsChild
                placeholder="Search..."
              />
            );
          }}
          shouldUnregister
          control={control}
          name="projectId"
        />
      </LabeledControl>
    </Modal>
  );
};
